import { useEffect, useRef } from 'react'

import { ClockConfigTypes } from 'modules/desktop/modules/widgets/components/clock/components'
import { WIDGETS_CONFIG } from 'modules/desktop/modules/widgets/constants/widgetsConfig'
import { useWidgetMutations } from 'modules/desktop/modules/widgets/hooks'
import { normalizeDisplayOrder } from 'modules/desktop/modules/widgets/utils'
import { resolveLayoutMode } from 'modules/desktop/pages/MainPage/components/DesktopGrid/utils'
import { useDeleteWidgetMutation } from 'shared/api/containerApi/requests'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { WidgetKey, WidgetSize } from 'shared/constants/widgets'
import { useUserContext } from 'shared/hooks/useUserContext'
import { logger } from 'shared/services/logger'
import { AiArtifactConfig, PersistedWidgetSpec } from 'shared/types/widgets'
import {
  dedupePersistedWidgetSpecs,
  widgetLogicalKey
} from 'shared/utils/dedupeWidgets'

// Caps retries on a persistently failing save so a network blip doesn't turn
// into a tight retry loop (currentItems changing on rollback re-triggers the
// effect immediately since hasRunForUserRef is cleared on error).
const MAX_MIGRATION_SAVE_ATTEMPTS = 3

// Historical shape of a widget entry as it was persisted on the desktop
// grid, before widgets moved back to the sidebar. Deliberately not the
// current `DesktopGridItem` union — that type no longer has a widget arm,
// but old localStorage payloads on a user's machine still do.
type RawGridWidgetItem = {
  id: string
  itemType: 'widget'
  widgetKey: WidgetKey
  widgetSize: WidgetSize
  position: { row: number; col: number }
  config?: ClockConfigTypes | AiArtifactConfig
}
type RawGridItem = { id: string; itemType: string } & Record<string, unknown>

const dateReviver = (key: string, value: unknown): unknown =>
  key === 'createdAt' && typeof value === 'string' ? new Date(value) : value

const readGridSlot = (
  key: LocalStorageKey.DESKTOP_GRID | LocalStorageKey.DESKTOP_GRID_MOBILE
): Record<string, RawGridItem[]> => {
  const raw = localStorage.getItem(key)
  if (!raw) return {}
  try {
    return (JSON.parse(raw, dateReviver) as Record<string, RawGridItem[]>) ?? {}
  } catch {
    return {}
  }
}

const writeGridSlot = (
  key: LocalStorageKey.DESKTOP_GRID | LocalStorageKey.DESKTOP_GRID_MOBILE,
  value: Record<string, RawGridItem[]>
) => {
  localStorage.setItem(key, JSON.stringify(value))
}

const isWidgetItem = (item: RawGridItem): item is RawGridWidgetItem =>
  item.itemType === 'widget'

const toPersistedSpec = (item: RawGridWidgetItem): PersistedWidgetSpec => ({
  id: item.id,
  widgetKey: item.widgetKey,
  size: item.widgetSize,
  displayOrder: 1,
  ...(item.config ? { config: item.config } : {})
})

/**
 * One-time cleanup for users who placed widgets on the desktop grid before
 * they moved back to the sidebar: pulls any leftover `itemType: 'widget'`
 * entries out of the per-viewport grid localStorage slots, converts them to
 * sidebar items ordered the way they were actually arranged on screen
 * (reading order: row, then column), and merges them into the sidebar.
 *
 * Self-clearing — once a user's grid slots no longer contain widgets (either
 * because they never had any, or because this ran once already), every
 * later mount is a no-op. No separate "already migrated" flag is needed.
 */
export const useMigrateGridWidgetsToSidebar = () => {
  const { user } = useUserContext()
  const {
    items: currentItems,
    canReorder,
    isSuccess,
    replaceWidgets
  } = useWidgetMutations()
  const deleteWidgetMutation = useDeleteWidgetMutation()
  const hasRunForUserRef = useRef<number | null>(null)
  const failedAttemptsRef = useRef(0)

  useEffect(() => {
    const userId = user?.id
    if (typeof userId !== 'number') return
    if (!isSuccess) return
    if (hasRunForUserRef.current === userId) return
    if (failedAttemptsRef.current >= MAX_MIGRATION_SAVE_ATTEMPTS) return
    hasRunForUserRef.current = userId

    const primaryMode = resolveLayoutMode(window.innerWidth)
    const primaryKey =
      primaryMode === 'sequential'
        ? LocalStorageKey.DESKTOP_GRID_MOBILE
        : LocalStorageKey.DESKTOP_GRID
    const fallbackKey =
      primaryKey === LocalStorageKey.DESKTOP_GRID
        ? LocalStorageKey.DESKTOP_GRID_MOBILE
        : LocalStorageKey.DESKTOP_GRID

    const primarySlot = readGridSlot(primaryKey)
    const fallbackSlot = readGridSlot(fallbackKey)
    const userIdStr = String(userId)

    const primaryItems = primarySlot[userIdStr] ?? []
    const fallbackItems = fallbackSlot[userIdStr] ?? []
    const orderingItems = primaryItems.some(isWidgetItem)
      ? primaryItems
      : fallbackItems

    const gridWidgets = orderingItems
      .filter(isWidgetItem)
      .sort(
        (a, b) =>
          a.position.row - b.position.row || a.position.col - b.position.col
      )

    if (gridWidgets.length === 0) return

    const keptSpecs: PersistedWidgetSpec[] = []
    for (const item of gridWidgets) {
      const config = WIDGETS_CONFIG[item.widgetKey]
      if (config?.isHidden?.(user) && config?.hiddenAction === 'remove') {
        deleteWidgetMutation.mutate(item.id)
        continue
      }
      keptSpecs.push(toPersistedSpec(item))
    }

    const dedupedMigrated = dedupePersistedWidgetSpecs(keptSpecs)

    const migratedIds = new Set(dedupedMigrated.map(spec => spec.id))
    const migratedKeys = new Set(
      dedupedMigrated
        .map(spec => widgetLogicalKey(spec))
        .filter((key): key is string => key !== null)
    )
    const untouchedExisting = currentItems.filter(item => {
      if (migratedIds.has(item.id)) return false
      const key = widgetLogicalKey(item)
      return key === null || !migratedKeys.has(key)
    })

    const merged = normalizeDisplayOrder([
      ...dedupedMigrated,
      ...untouchedExisting
    ])

    const stripWidgets = (slot: Record<string, RawGridItem[]>) => {
      const items = slot[userIdStr]
      if (!items) return slot
      return {
        ...slot,
        [userIdStr]: items.filter(item => !isWidgetItem(item))
      }
    }

    // this is the only chance to replace the server's placeholder
    // display_order values with the real, user-arranged ordering — only
    // strip the grid's leftover widget entries once that save actually
    // lands. Stripping unconditionally would make this self-clearing
    // migration permanently skip itself on the next load even if the save
    // failed (network blip, tab closing mid-request), leaving the backend
    // stuck on the old placeholder order with no way to retry.
    // If the settings query is unavailable, reuse existing rows and save only
    // the migrated widgets. With settings loaded, the shared writer preserves
    // hidden rows while saving the full visible order.
    const partialSpecs = dedupedMigrated.flatMap(item => {
      const key = widgetLogicalKey(item)
      const existing = currentItems.find(
        current =>
          current.id === item.id ||
          (key !== null && widgetLogicalKey(current) === key)
      )
      if (existing?.isForced) return []
      if (!existing) return [item]
      return [
        {
          ...existing,
          ...item,
          id: existing.id,
          displayOrder: existing.displayOrder
        }
      ]
    })
    const widgetsToSave = canReorder ? merged : partialSpecs
    const nextItems = canReorder
      ? merged
      : normalizeDisplayOrder([
          ...currentItems.map(
            current =>
              partialSpecs.find(spec => spec.id === current.id) ?? current
          ),
          ...partialSpecs.filter(
            item => !currentItems.some(current => current.id === item.id)
          )
        ])
    if (widgetsToSave.length === 0) {
      writeGridSlot(primaryKey, stripWidgets(primarySlot))
      writeGridSlot(fallbackKey, stripWidgets(fallbackSlot))
      return
    }
    replaceWidgets(nextItems, widgetsToSave, {
      onSuccess: () => {
        failedAttemptsRef.current = 0
        writeGridSlot(primaryKey, stripWidgets(primarySlot))
        writeGridSlot(fallbackKey, stripWidgets(fallbackSlot))
      },
      onError: () => {
        failedAttemptsRef.current += 1
        if (failedAttemptsRef.current >= MAX_MIGRATION_SAVE_ATTEMPTS) {
          logger.track('widget.migration_save_giving_up', {
            attempts: failedAttemptsRef.current
          })
          return
        }
        hasRunForUserRef.current = null
      }
    })
  }, [
    canReorder,
    currentItems,
    deleteWidgetMutation,
    isSuccess,
    replaceWidgets,
    user
  ])
}
